import FieldContainer from '@chargebee/chargebee-js-react-wrapper/dist/components/FieldContainer';
import Chargebee from '@chargebee/chargebee-js-types';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ensureError } from 'shared/utils/ErrorUtils';
import { loadChargebeeScript } from './loadChargebeeScript';
import { logError } from './Logger';

declare global {
  let Chargebee: Chargebee;
}

export interface TokenizeProps {
  firstName?: string;
  lastName?: string;
}

interface ChargebeeAddittionalInformationProps {
  braintree: ChargebeeCreditCard | undefined;
}

export interface ChargebeeCreditCard {
  creditCard: {
    details: {
      bin: string;
      cardType: string;
      cardholderName: string;
      expirationMonth: string;
      expirationYear: string;
      lastFour: string;
      lastTwo: string;
    };
  };
}

export interface ChargebeeResponse {
  additional_information: ChargebeeAddittionalInformationProps;
  token: string;
  vaultToken: string;
}

interface FieldError {
  errorCode:
    | 'card_number_incomplete'
    | 'card_number_invalid'
    | 'card_cvv_incomplete'
    | 'card_expiry_past';
  message: string;
}

export interface ChargebeeEvent extends ChangeEvent {
  error?: FieldError;
  field: 'number' | 'expiry' | 'cvv';
  complete?: boolean;
  empty: boolean;
  cardType: 'amex' | 'visa' | 'mastercard';
}

export interface ChargebeeErrorField {
  expiry: FieldError | null;
  cvv: FieldError | null;
  number: FieldError | null;
}

export const fonts = ['https://fonts.googleapis.com/css2?family=Inter'];

export function useChargebee(token?: string) {
  const cardRef = useRef<FieldContainer | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(!!window.Chargebee);

  useEffect(() => {
    if (!scriptLoaded) {
      loadChargebeeScript().then(() => setScriptLoaded(true));
    }
  }, [scriptLoaded]);

  const chargebeeInstance = useMemo(
    () => (scriptLoaded ? getChargebeeInstance(token) : null),
    [token, scriptLoaded],
  );

  const tokenize = async ({
    firstName,
    lastName,
  }: TokenizeProps): Promise<ChargebeeResponse> => {
    if (cardRef.current && Boolean(chargebeeInstance)) {
      try {
        const data = (await cardRef.current.tokenize({
          billingAddress: { firstName, lastName },
        })) as ChargebeeResponse;

        return data;
      } catch (err: unknown) {
        return Promise.reject(ensureError(err, 'Chargebee is not available'));
      }
    }

    return Promise.reject(new Error('Chargebee is not available'));
  };

  const addChargebeeCreditCard = async ({
    firstName,
    lastName,
  }: {
    firstName?: string;
    lastName?: string;
  }) => {
    return tokenize({ firstName, lastName });
  };

  return {
    cardRef,
    tokenize,
    isChargebeeAvailable: Boolean(chargebeeInstance),
    addChargebeeCreditCard,
    chargebeeInstance,
  };
}

function getChargebeeInstance(token?: string) {
  const chargebee = window.Chargebee;

  try {
    if (!chargebee || !token) {
      return null;
    }

    const instance = chargebee.init({
      site:
        import.meta.env.VITE_APP_TARGET === 'production'
          ? 'superdispatch'
          : 'superdispatch-test',
      publishableKey: token,
    });
    return instance;
  } catch (err: unknown) {
    const isError = err instanceof Error;
    logError(isError, 'ChargebeeWeb');

    return null;
  }
}
